/**
 * ONE GROUP HOME PAGE
 *
 * ⚠️ IMPORTANT: This page contains FIRST PASS MOCK DATA for the time being.
 *
 * Training modules are available in Company Knowledge that show how to update
 * the content for each section of this page with actual One Group content.
 *
 * Sections with training modules:
 * - Hero section copy
 * - Why Choose Us section
 * - How It Works steps
 * - Benefits descriptions
 * - Statistics/numbers
 * - Testimonials (real customer quotes)
 * - Call to action copy
 */

import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Stack,
  Container,
  Heading,
  Text,
  VStack,
  SimpleGrid,
  Icon,
  Divider,
  Button,
  HStack,
  AspectRatio,
  Avatar,
  Spinner,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  useDisclosure
} from "@chakra-ui/react";
import { useGoogleReviews, GoogleReview, GoogleReviewsData } from "../../../hooks/useGoogleReviews";

// Hook to detect when element is in viewport
const useInView = (options?: IntersectionObserverInit) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsInView(true);
        observer.disconnect(); // Only trigger once
      }
    }, { threshold: 0.3, ...options });

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return { ref, isInView };
};

// Animated counter component
const AnimatedNumber: React.FC<{
  value: number;
  prefix?: string;
  suffix?: string;
  duration?: number;
  isInView: boolean;
}> = ({ value, prefix = "", suffix = "", duration = 2000, isInView }) => {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    if (!isInView) return;

    let startTime: number;
    let animationFrame: number;

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);

      // Easing function for smooth deceleration
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setDisplayValue(Math.floor(easeOutQuart * value));

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [isInView, value, duration]);

  return <>{prefix}{displayValue.toLocaleString()}{suffix}</>;
};

// Stats section — figures sourced directly from each division's own home page
// (Solar: Systems Installed, Finance: Loans Settled, Property: Portfolio Value
// Managed & Client Satisfaction) so the numbers stay aligned across /solar /finance /property.
const StatsSection: React.FC = () => {
  const { ref, isInView } = useInView();

  const stats = [
    { value: 3007, label: "Systems Installed" },
    { value: 72, prefix: "$", suffix: "M", label: "Loans Settled" },
    { value: 100, prefix: "$", suffix: "M+", label: "Portfolio Value Managed" },
    { value: 95, suffix: "%", label: "Client Satisfaction" }
  ];

  return (
    <Box
      id="stats-section"
      ref={ref}
      position="relative"
      color="white"
      py={20}
      backgroundImage="url('https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=1920&q=80')"
      backgroundSize="cover"
      backgroundPosition="center"
      backgroundAttachment={{ base: "scroll", md: "fixed" }}
      _before={{
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        bg: "rgba(7, 30, 62, 0.88)",
        zIndex: 0
      }}
    >
      <Container maxW="container.xl" position="relative" zIndex={1}>
        <VStack spacing={8}>
          <VStack spacing={2}>
            <Text fontSize="sm" fontWeight="semibold" letterSpacing="0.22em" textTransform="uppercase" color="#C2A878">By the numbers</Text>
            <Heading size="xl" textAlign="center" textShadow="0 2px 4px rgba(0,0,0,0.3)">
              Our Impact in Numbers
            </Heading>
            <Box w="64px" h="2px" bg="#C2A878" mt={2} />
          </VStack>

          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={8} w="full" pt={8}>
            {stats.map((stat, idx) => (
              <VStack key={idx} spacing={2}>
                <Heading fontSize={{ base: "4xl", md: "6xl" }} color="#C2A878" whiteSpace="nowrap" sx={{ fontVariantNumeric: "tabular-nums" }} textShadow="0 2px 8px rgba(0,0,0,0.4)">
                  <AnimatedNumber
                    value={stat.value}
                    prefix={stat.prefix}
                    suffix={stat.suffix}
                    isInView={isInView}
                    duration={2000}
                  />
                </Heading>
                <Text fontSize="md" color="rgba(255, 255, 255, 0.9)" textShadow="0 1px 2px rgba(0,0,0,0.3)">
                  {stat.label}
                </Text>
              </VStack>
            ))}
          </SimpleGrid>
        </VStack>
      </Container>
    </Box>
  );
};

// Avatar fallback colors, cycled across merged reviews from all three divisions
const AVATAR_COLORS = ["#EA4335", "#34A853", "#4285F4", "#FBBC04"];

// Merges live Google reviews from all three divisions (Solar, Finance, Property)
// into a single round-robin feed so each visible row of 3 mixes all divisions.
function mergeDivisionReviews(sources: GoogleReviewsData[]): GoogleReview[] {
  const lists = sources.map((s) =>
    [...s.reviews].filter((r) => r.rating >= 4 && r.text).sort((a, b) => b.time - a.time)
  );
  const maxLen = Math.max(0, ...lists.map((l) => l.length));
  const merged: GoogleReview[] = [];
  for (let i = 0; i < maxLen; i++) {
    for (const list of lists) {
      if (list[i]) merged.push(list[i]);
    }
  }
  return merged;
}

// Reviews section showing 3 at a time with navigation, auto-rotating across
// live Google reviews pooled from all three One Group divisions.
const ReviewsSection: React.FC = () => {
  const solar = useGoogleReviews(process.env.REACT_APP_GOOGLE_PLACE_ID || "");
  const finance = useGoogleReviews(process.env.REACT_APP_GOOGLE_PLACE_ID_FINANCE || "");
  const property = useGoogleReviews(process.env.REACT_APP_GOOGLE_PLACE_ID_PROPERTY || "");

  const loading = solar.loading || finance.loading || property.loading;
  const sources = [solar.data, finance.data, property.data].filter(
    (d): d is GoogleReviewsData => d !== null
  );

  const merged = React.useMemo(() => mergeDivisionReviews(sources), [sources]);

  const reviewsPerPage = 3;
  const totalPages = Math.max(1, Math.ceil(merged.length / reviewsPerPage));
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visible, setVisible] = useState(true);

  const goToIndex = (index: number) => {
    setVisible(false);
    setTimeout(() => {
      setCurrentIndex(index);
      setVisible(true);
    }, 250);
  };

  const nextReviews = () => goToIndex((currentIndex + 1) % totalPages);
  const prevReviews = () => goToIndex((currentIndex - 1 + totalPages) % totalPages);

  // Auto-rotate through the pooled reviews every 6s
  useEffect(() => {
    if (totalPages <= 1) return;
    const interval = setInterval(nextReviews, 6000);
    return () => clearInterval(interval);
  }, [totalPages, currentIndex]);

  const visibleReviews = merged.slice(
    currentIndex * reviewsPerPage,
    currentIndex * reviewsPerPage + reviewsPerPage
  );

  const aggregateTotal = sources.reduce((sum, s) => sum + s.totalRatings, 0);
  const aggregateRating = aggregateTotal > 0
    ? sources.reduce((sum, s) => sum + s.rating * s.totalRatings, 0) / aggregateTotal
    : 0;
  const fullStars = Math.floor(aggregateRating);
  const hasHalfStar = aggregateRating - fullStars >= 0.5;

  if (loading) {
    return (
      <Box id="testimonials-section" bg="#f7f3ec" py={16} textAlign="center">
        <Spinner color="#C2A878" size="lg" />
      </Box>
    );
  }

  if (merged.length === 0) return null;

  return (
    <Box id="testimonials-section" bg="#f7f3ec" py={16} overflow="hidden">
      <Container maxW="container.xl">
        <VStack spacing={8}>
          {/* Header */}
          <Heading size="xl" textAlign="center" color="#071e3e">
            What Our Clients Say
          </Heading>
          <HStack spacing={2} justify="center">
            <Box
              as="img"
              src="https://www.google.com/favicon.ico"
              alt="Google"
              boxSize={6}
              display="inline-block"
            />
            <Text fontSize="2xl" fontWeight="bold" color="#071e3e">
              {aggregateRating.toFixed(1)}
            </Text>
            <HStack spacing={0.5}>
              {[1, 2, 3, 4, 5].map((star) => (
                <Icon
                  key={star}
                  as={star === fullStars + 1 && hasHalfStar ? FaStarHalfAlt : FaStar}
                  color={star <= fullStars || (star === fullStars + 1 && hasHalfStar) ? "#FBBC04" : "gray.300"}
                  boxSize={5}
                />
              ))}
            </HStack>
            <Text fontSize="sm" color="gray.500">
              Based on {aggregateTotal} reviews
            </Text>
          </HStack>

          {/* Reviews Grid - 3 at a time with arrows, fading between pages */}
          <HStack spacing={4} w="full" mt={8} align="center">
            {/* Left Arrow */}
            <Box
              as="button"
              onClick={prevReviews}
              p={3}
              borderRadius="full"
              bg="white"
              boxShadow="md"
              border="1px solid"
              borderColor="gray.200"
              cursor={totalPages <= 1 ? "not-allowed" : "pointer"}
              opacity={totalPages <= 1 ? 0.5 : 1}
              _hover={totalPages > 1 ? { bg: "gray.100", boxShadow: "lg" } : {}}
              transition="all 0.2s"
              flexShrink={0}
              display={{ base: "none", md: "flex" }}
              alignItems="center"
              justifyContent="center"
            >
              <Icon as={FaChevronLeft} color="gray.600" boxSize={5} />
            </Box>

            <SimpleGrid
              columns={{ base: 1, md: 3 }}
              spacing={6}
              flex="1"
              opacity={visible ? 1 : 0}
              transition="opacity 0.25s ease-in-out"
            >
              {visibleReviews.map((review, idx) => (
                <Box
                  key={`${review.time}-${idx}`}
                  p={6}
                  bg="white"
                  borderRadius="xl"
                  boxShadow="md"
                  border="1px solid"
                  borderColor="gray.200"
                  transition="all 0.3s"
                  _hover={{ boxShadow: "lg", transform: "translateY(-2px)" }}
                >
                  <HStack spacing={4} align="start">
                    <Avatar
                      src={review.profile_photo_url}
                      name={review.author_name}
                      size="md"
                      bg={AVATAR_COLORS[idx % AVATAR_COLORS.length]}
                    />

                    {/* Review Content */}
                    <VStack align="start" spacing={1} flex="1">
                      <HStack justify="space-between" w="full">
                        <Text fontWeight="600" color="#071e3e">
                          {review.author_name}
                        </Text>
                      </HStack>
                      <HStack spacing={0.5}>
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Icon
                            key={star}
                            as={FaStar}
                            color={star <= review.rating ? "#FBBC04" : "gray.300"}
                            boxSize={3}
                          />
                        ))}
                        <Text fontSize="xs" color="gray.500" ml={2}>
                          {review.relative_time_description}
                        </Text>
                      </HStack>
                    </VStack>
                  </HStack>
                  <Text color="gray.600" fontSize="sm" mt={4} lineHeight="1.6">
                    "{review.text}"
                  </Text>
                </Box>
              ))}
            </SimpleGrid>

            {/* Right Arrow */}
            <Box
              as="button"
              onClick={nextReviews}
              p={3}
              borderRadius="full"
              bg="white"
              boxShadow="md"
              border="1px solid"
              borderColor="gray.200"
              cursor={totalPages <= 1 ? "not-allowed" : "pointer"}
              opacity={totalPages <= 1 ? 0.5 : 1}
              _hover={totalPages > 1 ? { bg: "gray.100", boxShadow: "lg" } : {}}
              transition="all 0.2s"
              flexShrink={0}
              display={{ base: "none", md: "flex" }}
              alignItems="center"
              justifyContent="center"
            >
              <Icon as={FaChevronRight} color="gray.600" boxSize={5} />
            </Box>
          </HStack>

          {/* Mobile Navigation Arrows */}
          <HStack spacing={4} display={{ base: "flex", md: "none" }} pt={4}>
            <Box
              as="button"
              onClick={prevReviews}
              p={3}
              borderRadius="full"
              bg="white"
              boxShadow="md"
              border="1px solid"
              borderColor="gray.200"
              cursor={totalPages <= 1 ? "not-allowed" : "pointer"}
              opacity={totalPages <= 1 ? 0.5 : 1}
              _hover={totalPages > 1 ? { bg: "gray.100" } : {}}
            >
              <Icon as={FaChevronLeft} color="gray.600" boxSize={5} />
            </Box>
            <Box
              as="button"
              onClick={nextReviews}
              p={3}
              borderRadius="full"
              bg="white"
              boxShadow="md"
              border="1px solid"
              borderColor="gray.200"
              cursor={totalPages <= 1 ? "not-allowed" : "pointer"}
              opacity={totalPages <= 1 ? 0.5 : 1}
              _hover={totalPages > 1 ? { bg: "gray.100" } : {}}
            >
              <Icon as={FaChevronRight} color="gray.600" boxSize={5} />
            </Box>
          </HStack>
        </VStack>
      </Container>
    </Box>
  );
};

// Video testimonials — client stories with click-to-play YouTube embeds
const VIDEO_TESTIMONIALS = [
  { name: "Ryan", label: "QLD Homeowner", videoId: "lMWgA1VoINY" },
  { name: "Mel", label: "3-BDR Homeowner", videoId: "GcqFqG2RpRQ" },
  { name: "Deik", label: "Lack Group", videoId: "TEqnvl2vTbs" }
];

const VideoTestimonialsSection: React.FC = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [activeVideoId, setActiveVideoId] = useState<string | null>(null);

  return (
    <Box bg="#f7f3ec" py={16}>
      <Container maxW="container.xl">
        <VStack spacing={4} mb={10} textAlign="center">
          <Heading size="xl" color="#071e3e">
            Hear From Our Clients
          </Heading>
          <Text fontSize="lg" color="gray.600" maxW="2xl">
            Real stories from people who've built their Prosperity Engine™ with us.
          </Text>
        </VStack>

        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
          {VIDEO_TESTIMONIALS.map((t) => (
            <Box
              key={t.name}
              as="button"
              onClick={() => {
                setActiveVideoId(t.videoId);
                onOpen();
              }}
              position="relative"
              borderRadius="xl"
              overflow="hidden"
              boxShadow="md"
              border="1px solid"
              borderColor="gray.200"
              _hover={{ boxShadow: "xl", borderColor: "#C2A878" }}
              transition="all 0.3s"
              textAlign="left"
            >
              <AspectRatio ratio={16 / 9}>
                <Box
                  as="img"
                  src={`https://img.youtube.com/vi/${t.videoId}/hqdefault.jpg`}
                  alt={`${t.name} video testimonial`}
                  objectFit="cover"
                />
              </AspectRatio>
              <Box position="absolute" inset={0} bg="blackAlpha.300" display="flex" alignItems="center" justifyContent="center">
                <Box bg="whiteAlpha.900" borderRadius="full" p={4} boxShadow="lg">
                  <Icon as={FaPlay} color="#071e3e" boxSize={5} />
                </Box>
              </Box>
              <Box position="absolute" bottom={0} left={0} right={0} p={3} bgGradient="linear(to-t, blackAlpha.700, transparent)">
                <Text fontWeight="700" color="white" fontSize="sm">{t.name}</Text>
                <Text fontSize="xs" color="whiteAlpha.800">{t.label}</Text>
              </Box>
            </Box>
          ))}
        </SimpleGrid>
      </Container>

      <Modal
        isOpen={isOpen}
        onClose={() => {
          onClose();
          setActiveVideoId(null);
        }}
        size="3xl"
        isCentered
      >
        <ModalOverlay />
        <ModalContent bg="black">
          <ModalCloseButton color="white" zIndex={1} />
          <ModalBody p={0}>
            {activeVideoId && (
              <AspectRatio ratio={16 / 9}>
                <iframe
                  src={`https://www.youtube.com/embed/${activeVideoId}?autoplay=1`}
                  title="Client video testimonial"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </AspectRatio>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

// How It Works section with sequentially animated arrows
const HowItWorksSection: React.FC = () => {
  const { ref, isInView } = useInView({ threshold: 0.2 });
  const [visibleArrows, setVisibleArrows] = useState<number[]>([]);

  const steps = [
    {
      step: "01",
      title: "Discovery Session",
      description: "A free, no-pressure conversation to map your current position across health, energy, finances, and assets."
    },
    {
      step: "02",
      title: "Prosperity Blueprint",
      description: "We design a structured plan across all four divisions — Water, Solar, Finance, and Property — tailored to your long-term goals."
    },
    {
      step: "03",
      title: "Strategic Execution",
      description: "Our specialists coordinate across divisions to implement each pillar in the right sequence, maximising compounding effect."
    },
    {
      step: "04",
      title: "Lifetime Partnership",
      description: "We stay with you for the long term — reviewing, optimising, and evolving your Prosperity Engine™ as your life grows."
    }
  ];

  useEffect(() => {
    if (!isInView) return;

    // Animate arrows one by one with 400ms delay between each
    const timers: NodeJS.Timeout[] = [];
    for (let i = 0; i < steps.length - 1; i++) {
      const timer = setTimeout(() => {
        setVisibleArrows(prev => [...prev, i]);
      }, 600 + i * 400); // Start after 600ms, then 400ms between each
      timers.push(timer);
    }

    return () => timers.forEach(t => clearTimeout(t));
  }, [isInView]);

  return (
    <Box id="process-section" ref={ref} bg="#071e3e" py={16} borderTop="3px solid #C2A878">
      <Container maxW="container.xl">
        <VStack spacing={8}>
          <Heading size="xl" textAlign="center" color="white">
            How The Prosperity Engine™ Works
          </Heading>
          <Text fontSize="lg" textAlign="center" color="gray.300" maxW="2xl">
            Prosperity is not built in one decision. It is built in a system.
            Here is how we build yours.
          </Text>

          <Box pt={8} w="full">
            {/* Desktop layout with horizontal arrows */}
            <HStack
              display={{ base: "none", md: "flex" }}
              spacing={0}
              justify="center"
              align="flex-start"
            >
              {steps.map((step, idx, arr) => (
                <React.Fragment key={idx}>
                  <VStack spacing={4} flex="1" maxW="220px">
                    <Box
                      w="60px"
                      h="60px"
                      borderRadius="full"
                      bg="#C2A878"
                      color="#071e3e"
                      fontFamily="'Playfair Display', Georgia, serif"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      fontSize="xl"
                      fontWeight="bold"
                    >
                      {step.step}
                    </Box>
                    <Heading size="sm" textAlign="center" color="white">
                      {step.title}
                    </Heading>
                    <Text color="gray.300" textAlign="center" fontSize="md">
                      {step.description}
                    </Text>
                  </VStack>
                  {idx < arr.length - 1 && (
                    <Box
                      display="flex"
                      alignItems="center"
                      pt="18px"
                      px={4}
                      opacity={visibleArrows.includes(idx) ? 1 : 0}
                      transform={visibleArrows.includes(idx) ? "translateX(0)" : "translateX(-10px)"}
                      transition="all 0.4s ease-out"
                    >
                      <Icon
                        as={FaChevronRight}
                        boxSize={6}
                        color="white"
                      />
                    </Box>
                  )}
                </React.Fragment>
              ))}
            </HStack>

            {/* Mobile layout with vertical arrows */}
            <VStack display={{ base: "flex", md: "none" }} spacing={0}>
              {steps.map((step, idx, arr) => (
                <React.Fragment key={idx}>
                  <VStack spacing={4}>
                    <Box
                      w="60px"
                      h="60px"
                      borderRadius="full"
                      bg="#C2A878"
                      color="#071e3e"
                      fontFamily="'Playfair Display', Georgia, serif"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      fontSize="xl"
                      fontWeight="bold"
                    >
                      {step.step}
                    </Box>
                    <Heading size="sm" textAlign="center" color="white">
                      {step.title}
                    </Heading>
                    <Text color="gray.300" textAlign="center" fontSize="sm" maxW="280px">
                      {step.description}
                    </Text>
                  </VStack>
                  {idx < arr.length - 1 && (
                    <Box
                      py={4}
                      opacity={visibleArrows.includes(idx) ? 1 : 0}
                      transform={visibleArrows.includes(idx) ? "translateY(0)" : "translateY(-10px)"}
                      transition="all 0.4s ease-out"
                    >
                      <Icon
                        as={FaChevronDown}
                        boxSize={6}
                        color="white"
                      />
                    </Box>
                  )}
                </React.Fragment>
              ))}
            </VStack>
          </Box>
        </VStack>
      </Container>
    </Box>
  );
};

import { Link as RouterLink } from "react-router-dom";
import { FaCheckCircle, FaStar, FaStarHalfAlt, FaSun, FaCoins, FaHome, FaTint, FaChevronRight, FaChevronLeft, FaChevronDown, FaPlay } from "react-icons/fa";
import { brandConfig } from "../../../brandConfig";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";
import { DevSectionBadges } from "../../../components/DevSectionBadges";
import AdminModuleLinks from "../../../components/AdminModuleLinks";

const Home: React.FC = () => {
  return (
    <Box minH="100vh" bg="white" sx={{ "& .chakra-heading": { fontFamily: "'Playfair Display', Georgia, 'Times New Roman', serif", fontWeight: 600, letterSpacing: "-0.01em" } }}>
      <SEOHead
        pagePath="/"
        fallbackTitle="One Group | Finance, Solar & Property Solutions"
        fallbackDescription="One Group provides integrated finance, solar energy, and property investment solutions across Australia."
      />
      <NavbarWithCallToAction />

      {/* Quick links — only for a logged-in user with at least one viewable module; anonymous visitors see nothing here */}
      <AdminModuleLinks />

      {/* Video Section */}
      {/* Navy blue banner - full width */}
      <Box
        position="relative"
        bg="#071e3e"
        w="100%"
        py={12}
        _before={{
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          bg: "#071e3e",
          zIndex: 0,
          opacity: 1,
        }}
      >
        <Container maxW="container.lg" position="relative" zIndex={1}>
          <Box
            border="4px solid white"
            borderRadius="12px"
            overflow="hidden"
            boxShadow="0 15px 45px rgba(0, 0, 0, 0.5), 0 6px 20px rgba(0, 0, 0, 0.35)"
          >
            <AspectRatio ratio={16 / 9}>
              <video
                src="https://files-mac-studio.vanguardiq.com.au/ipfs/QmQGxh8eZHtn1KxHwDTZ8XwLgovEoFPmfgg4n7C7NEJaLu"
                autoPlay
                muted
                loop
                playsInline
                style={{ borderRadius: "8px", width: "100%", height: "100%" }}
              />
            </AspectRatio>
          </Box>
        </Container>
      </Box>

      {/* One Group Hero Section */}
      <Box
        id="hero-section"
        position="relative"
        color="white"
        pt={10}
        pb={20}
        w="100%"
        backgroundImage="url('https://images.unsplash.com/photo-1497366216548-37526070297c?w=1920&q=80')"
        backgroundSize="cover"
        backgroundPosition="center"
        backgroundRepeat="no-repeat"
        backgroundAttachment={{ base: "scroll", md: "fixed" }}
        _before={{
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          bg: "rgba(7, 30, 62, 0.78)",
          zIndex: 0
        }}
      >
        <Container maxW="container.xl" position="relative" zIndex={1}>
          <VStack spacing={6} textAlign="center">
            <Text fontSize="sm" fontWeight="semibold" letterSpacing="0.24em" textTransform="uppercase" color="#C2A878">Property · Finance · Solar · Water</Text>
            <Heading fontSize={{ base: "4xl", md: "6xl" }} maxW="4xl" lineHeight="1.05">
              {brandConfig.siteName}
            </Heading>
            <Box w="72px" h="2px" bg="#C2A878" />
            <Heading size="lg" fontWeight="normal" color="gray.300">
              {brandConfig.tagline}
            </Heading>
            <Text fontSize="xl" maxW="3xl" color="gray.400">
              {brandConfig.description}
            </Text>
            <Stack direction={{ base: "column", sm: "row" }} spacing={4} pt={4} w={{ base: "100%", sm: "auto" }}>
              <Button as={RouterLink} to="/contact" bg="#C2A878" color="#071e3e" _hover={{ bg: "#b09060" }} size="lg" fontWeight="bold">
                Book a Free Consultation
              </Button>
              <Button as={RouterLink} to="/about" variant="outline" borderColor="white" color="white" _hover={{ bg: "rgba(255, 255, 255, 0.1)" }} size="lg">
                Our Story
              </Button>
            </Stack>
          </VStack>
        </Container>
      </Box>

      {/* 4-Division Navigation Cards — The Prosperity Engine™ */}
      <Container id="services-section" maxW="container.xl" py={16}>
        <VStack spacing={8}>
          <Heading size="xl" textAlign="center" color="#071e3e">
            The Prosperity Engine™
          </Heading>
          <Text fontSize="lg" textAlign="center" color="gray.600" maxW="2xl">
            One Group Australia integrates Health, Efficiency, Structure, and Growth —
            four divisions working together to build your long-term prosperity.
          </Text>

          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6} w="full">
            {/* One Water Australia */}
            <RouterLink to="/water">
              <Box
                p={8}
                borderRadius="lg"
                textAlign="center"
                cursor="pointer"
                _hover={{ transform: "translateY(-4px)", shadow: "2xl" }}
                transition="all 0.3s"
                position="relative"
                overflow="hidden"
                minH="280px"
                display="flex"
                flexDirection="column"
                justifyContent="center"
                backgroundImage="url('https://images.unsplash.com/photo-1548839140-29a749e1cf4d?w=800&q=80')"
                backgroundSize="cover"
                backgroundPosition="center"
                border="3px solid"
                borderColor="#6FAFD6"
                _before={{
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  bg: "rgba(0, 0, 0, 0.60)",
                  zIndex: 0
                }}
              >
                <Box position="relative" zIndex={1}>
                  <Icon as={FaTint} boxSize={12} color="#6FAFD6" mb={4} />
                  <Heading size="md" color="white" mb={2}>
                    One Water Australia
                  </Heading>
                  <Text color="white" mb={4} fontSize="sm">
                    Know what's in your water.
                  </Text>
                  <Button bg="#6FAFD6" color="white" variant="solid" size="sm" _hover={{ bg: "#5a9abf" }}>
                    Explore Water →
                  </Button>
                </Box>
              </Box>
            </RouterLink>

            {/* One Solar Australia */}
            <RouterLink to="/solar">
              <Box
                p={8}
                borderRadius="lg"
                textAlign="center"
                cursor="pointer"
                _hover={{ transform: "translateY(-4px)", shadow: "2xl" }}
                transition="all 0.3s"
                position="relative"
                overflow="hidden"
                minH="280px"
                display="flex"
                flexDirection="column"
                justifyContent="center"
                backgroundImage="url('https://images.unsplash.com/photo-1509391366360-2e959784a276?w=800&q=80')"
                backgroundSize="cover"
                backgroundPosition="center"
                border="3px solid"
                borderColor="#C69214"
                _before={{
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  bg: "rgba(0, 0, 0, 0.55)",
                  zIndex: 0
                }}
              >
                <Box position="relative" zIndex={1}>
                  <Icon as={FaSun} boxSize={12} color="#C69214" mb={4} />
                  <Heading size="md" color="white" mb={2}>
                    One Solar Australia
                  </Heading>
                  <Text color="white" mb={4} fontSize="sm">
                    SWITCH. ADD. UPGRADE.
                  </Text>
                  <Button bg="#C69214" color="white" variant="solid" size="sm" _hover={{ bg: "#a07e10" }}>
                    Explore Solar →
                  </Button>
                </Box>
              </Box>
            </RouterLink>

            {/* One Finance Australia */}
            <RouterLink to="/finance">
              <Box
                p={8}
                borderRadius="lg"
                textAlign="center"
                cursor="pointer"
                _hover={{ transform: "translateY(-4px)", shadow: "2xl" }}
                transition="all 0.3s"
                position="relative"
                overflow="hidden"
                minH="280px"
                display="flex"
                flexDirection="column"
                justifyContent="center"
                backgroundImage="url('https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800&q=80')"
                backgroundSize="cover"
                backgroundPosition="center"
                border="3px solid"
                borderColor="#C2A878"
                _before={{
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  bg: "rgba(0, 0, 0, 0.65)",
                  zIndex: 0
                }}
              >
                <Box position="relative" zIndex={1}>
                  <Icon as={FaCoins} boxSize={12} color="#C2A878" mb={4} />
                  <Heading size="md" color="white" mb={2}>
                    One Finance Australia
                  </Heading>
                  <Text color="white" mb={4} fontSize="sm">
                    Structured for what's next.
                  </Text>
                  <Button bg="#C2A878" color="#071e3e" variant="solid" size="sm" _hover={{ bg: "#b09060" }}>
                    Explore Finance →
                  </Button>
                </Box>
              </Box>
            </RouterLink>

            {/* One Property Australia */}
            <RouterLink to="/property">
              <Box
                p={8}
                borderRadius="lg"
                textAlign="center"
                cursor="pointer"
                _hover={{ transform: "translateY(-4px)", shadow: "2xl" }}
                transition="all 0.3s"
                position="relative"
                overflow="hidden"
                minH="280px"
                display="flex"
                flexDirection="column"
                justifyContent="center"
                backgroundImage="url('https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&q=80')"
                backgroundSize="cover"
                backgroundPosition="center"
                border="3px solid"
                borderColor="#0D7F6E"
                _before={{
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  bg: "rgba(0, 0, 0, 0.55)",
                  zIndex: 0
                }}
              >
                <Box position="relative" zIndex={1}>
                  <Icon as={FaHome} boxSize={12} color="#0D7F6E" mb={4} />
                  <Heading size="md" color="white" mb={2}>
                    One Property Australia
                  </Heading>
                  <Text color="white" mb={4} fontSize="sm">
                    Real people. Real conversations. Real results.
                  </Text>
                  <Button bg="#0D7F6E" color="white" variant="solid" size="sm" _hover={{ bg: "#0a6459" }}>
                    Explore Property →
                  </Button>
                </Box>
              </Box>
            </RouterLink>
          </SimpleGrid>
        </VStack>
      </Container>

      <Divider />

      {/* Why Choose One Group Section */}
      <Box id="features-section" bg="#f7f3ec" py={16}>
        <Container maxW="container.xl">
          <VStack spacing={8}>
            <Heading size="xl" textAlign="center" color="#071e3e">
              We Design Systems. Not Transactions.
            </Heading>
            <Text fontSize="lg" textAlign="center" color="gray.600" maxW="2xl">
              One Group Australia is structured for the long term. Every division works
              together inside The Prosperity Engine™ — so your decisions compound, not compete.
            </Text>

            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={8} pt={8}>
              {[
                {
                  title: "The Prosperity Engine™",
                  description: "Health, Efficiency, Structure, and Growth — four divisions coordinated into one long-term strategy for lasting Australian prosperity."
                },
                {
                  title: "Structural Thinking",
                  description: "We don't sell products. We architect systems that connect your health, energy, finances, and assets into a unified plan."
                },
                {
                  title: "Education Before Selling",
                  description: "We lead with clarity. Our advisors help you understand your position before any recommendation is made."
                },
                {
                  title: "Cross-Division Integration",
                  description: "Solar reduces costs that improve borrowing capacity. Finance structures the capital that builds your property portfolio. Water protects the foundation."
                },
                {
                  title: "Long-Term Partnerships",
                  description: "We build multi-decade relationships — not single transactions. Our goal is lifetime client outcomes, not short-term revenue."
                },
                {
                  title: "Calm Authority",
                  description: "No hype. No pressure. Structured, intelligent guidance from specialists who think in decades, not quarters."
                }
              ].map((benefit, idx) => (
                <Box
                  key={idx}
                  p={6}
                  bg="white"
                  borderRadius="lg"
                  border="1px solid"
                  borderColor="gray.200"
                  borderLeftWidth="4px"
                  borderLeftColor="#C2A878"
                >
                  <HStack spacing={3} mb={3}>
                    <Icon as={FaCheckCircle} color="#071e3e" boxSize={5} />
                    <Heading size="sm" color="#071e3e">
                      {benefit.title}
                    </Heading>
                  </HStack>
                  <Text color="gray.600" fontSize="sm">
                    {benefit.description}
                  </Text>
                </Box>
              ))}
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>

      <Divider />

      {/* How It Works Section */}
      <HowItWorksSection />

      {/* Stats Section */}
      <StatsSection />

      <Divider />

      {/* Video Testimonials */}
      <VideoTestimonialsSection />

      <Divider />

      {/* Google Reviews Section - 3 at a time */}
      <ReviewsSection />

      <Divider />

      {/* Call to Action Section */}
      <Box id="cta-section" bg="#071e3e" color="white" py={20}>
        <Container maxW="container.lg">
          <VStack spacing={6} textAlign="center">
            <Heading size="xl">
              Begin Building Your Prosperity Engine™
            </Heading>
            <Text fontSize="lg" color="gray.300" maxW="2xl">
              Water protects the foundation. Solar improves cash flow. Finance structures
              opportunity. Property builds wealth. Start the conversation — no pressure, no hype.
            </Text>
            <Stack direction={{ base: "column", sm: "row" }} spacing={4} pt={4} w={{ base: "100%", sm: "auto" }}>
              <Button as={RouterLink} to="/contact" bg="#C2A878" color="#071e3e" _hover={{ bg: "#b09060" }} size="lg" fontWeight="bold">
                Book Free Consultation
              </Button>
              <Button as={RouterLink} to="/solutions" variant="outline" borderColor="white" color="white" _hover={{ bg: "rgba(255, 255, 255, 0.1)" }} size="lg">
                Explore All Divisions
              </Button>
            </Stack>
          </VStack>
        </Container>
      </Box>

      <FooterWithFourColumns />
      <DevSectionBadges />
    </Box>
  );
};

export default Home;
